import { copyObject } from '@/lib/objects'
import { formatDateRu } from '@/lib/datetime'
import { TA_LP_SHEET_PREFIX } from '@/components/expAnalogs/const'

function getFormatValue(value = '') {
  if (typeof value !== 'number' || Number.isInteger(value)) return value
  return Number(value).toFixed(2)
}

function getZeroDisplayFromNumFmt(cell) {
  const numFmt = String(cell?.numFmt ?? '').trim()
  if (!numFmt) return ''
  const parts = numFmt.split(';')
  const zeroPartRaw = (parts[2] || '').trim()
  if (!zeroPartRaw) return ''
  const zeroPart = zeroPartRaw.replace(/\\./g, '').replace(/"([^"]*)"/g, '$1').trim()
  if (!zeroPart) return ''
  if (zeroPart.includes('~')) return '~'
  if (zeroPart.includes('-')) return '-'
  if (/^0+(?:[.,]0+)?$/.test(zeroPart)) return '0'
  return ''
}

function formulaFallbackDisplay(cell) {
  const text = String(cell?.text ?? '').trim()
  if (text) return text
  const fmt = getZeroDisplayFromNumFmt(cell)
  if (fmt) return fmt
  return '0'
}

function formatCellValue(v) {
  if (v instanceof Date) return formatDateRu(v)
  if (typeof v === 'number') return String(getFormatValue(v))
  return String(v)
}

function cellToString(cell) {
  if (!cell || cell.value === undefined || cell.value === null) return ''

  const v = cell.value
  if (v instanceof Date) return formatDateRu(v)
  if (typeof v === 'object') {
    if (v.richText) {
      return v.richText.map(item => item.text || '').join(' ')
    }
    if (Object.prototype.hasOwnProperty.call(v, 'text') && v.text != null) {
      return String(v.text)
    }
    if (Object.prototype.hasOwnProperty.call(v, 'result')) {
      const text = String(cell.text ?? '').trim()
      if (text) return text
      const r = v.result
      if (r !== undefined && r !== null) return formatCellValue(r)
      return ''
    }
    if (
      Object.prototype.hasOwnProperty.call(v, 'formula') ||
      Object.prototype.hasOwnProperty.call(v, 'sharedFormula')
    ) {
      const r = v.result
      if (r !== undefined && r !== null) return formatCellValue(r)
      return formulaFallbackDisplay(cell)
    }
  }
  if (cell.text != null && String(cell.text).trim() !== '') {
    return String(cell.text)
  }
  return formatCellValue(v)
}

function getMergedSpan(mergeModel) {
  if (!mergeModel) return null
  const top = mergeModel.top ?? 0
  const bottom = mergeModel.bottom ?? 0
  const left = mergeModel.left ?? 0
  const right = mergeModel.right ?? 0
  const rowspan = bottom - top + 1
  const colspan = right - left + 1
  if (rowspan <= 1 && colspan <= 1) return null
  return { rowspan, colspan }
}

/** @param {import('exceljs').Worksheet} sheet */
export function sheetToDisplayRows(sheet) {
  let detectedMaxCol = 0
  const maxRow = sheet.rowCount || 0
  for (let r = 1; r <= maxRow; r++) {
    const row = sheet.getRow(r)
    row.eachCell({ includeEmpty: false }, (_cell, col) => {
      if (col > detectedMaxCol) detectedMaxCol = col
    })
  }
  const merges = sheet._merges || {}
  for (const merge of Object.values(merges)) {
    const right = merge?.model?.right ?? 0
    if (right > detectedMaxCol) detectedMaxCol = right
  }
  // Только реально занятые колонки (без «хвоста» из пустых ячеек Excel).
  const maxCol = Math.min(Math.max(detectedMaxCol, 1), 20)
  const rows = []

  for (let r = 1; r <= maxRow; r++) {
    const row = sheet.getRow(r)
    const cells = []
    let any = false
    for (let c = 1; c <= maxCol; c++) {
      const cell = row.getCell(c)
      let master = cell
      try {
        if (cell.isMerged && cell.master) master = cell.master
      } catch {
        master = cell
      }
      let s = cellToString(master)
      if (!String(s ?? '').trim() && master !== cell) {
        s = cellToString(cell)
      }
      const value = s != null ? String(s) : ''
      const mergeModel = merges[master.address]?.model || merges[cell.address]?.model
      const span = getMergedSpan(mergeModel)
      const isMergedSlave = cell.isMerged && master.address !== cell.address
      cells.push({
        value,
        merged: isMergedSlave,
        mergeObj: span,
        hidden: isMergedSlave,
        rowspan: span?.rowspan || 1,
        colspan: span?.colspan || 1,
      })
      if (value.trim()) any = true
    }
    if (any) rows.push(cells)
  }
  return trimTrailingEmptyColumns(rows)
}

/** Убирает хвостовые пустые колонки, если Excel оставил форматирование правее данных. */
export function trimTrailingEmptyColumns(rows) {
  if (!rows.length) return rows
  let maxUsed = 0
  for (const row of rows) {
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (!cell || cell.hidden) continue
      const hasValue = String(cell.value ?? '').trim() !== ''
      const spanEnd = c + (Number(cell.colspan) || 1)
      if (hasValue || spanEnd > c + 1) {
        if (spanEnd > maxUsed) maxUsed = spanEnd
      }
    }
  }
  if (maxUsed <= 0 || maxUsed >= (rows[0]?.length || 0)) return rows
  return rows.map(row => row.slice(0, maxUsed))
}

function findFirstNoteSectionRowIndex(rows, fromIdx = 0) {
  const from = Math.max(0, Number(fromIdx) || 0)
  for (let i = from; i < rows.length; i++) {
    const parts = (rows[i] || []).map(c => String(c?.value ?? '').trim())
    const tight = parts.join('').toLowerCase()
    const spaced = parts.filter(Boolean).join(' ').toLowerCase()
    if (tight.includes('примечани') || spaced.includes('примечани')) return i
  }
  return -1
}

function collectNoteRows(rows, startIdx) {
  const from = Math.max(0, Number(startIdx) || 0)
  const lines = []
  for (let r = from; r < rows.length; r++) {
    const line = (rows[r] || [])
      .map(c => String(c?.value || '').trim())
      .filter(Boolean)
      .join(' ')
      .trim()
    if (line) lines.push(line)
  }
  return lines.join('\n\n').trim()
}

function findMnnSheet(workbook) {
  for (const ws of workbook.worksheets || []) {
    if (String(ws.name || '').trim().startsWith(TA_LP_SHEET_PREFIX)) {
      return ws
    }
  }
  return null
}

/**
 * Excel с листом, имя которого начинается с «МНН».
 * @param {import('exceljs').Workbook} workbook
 */
export function parseTaLpWorkbook(workbook) {
  const ws = findMnnSheet(workbook)
  if (!ws) {
    throw new Error(
      `В файле не найден лист, имя которого начинается с «${TA_LP_SHEET_PREFIX}».`
    )
  }

  const allRows = sheetToDisplayRows(ws)
  let tableEnd = allRows.length
  let note = ''
  const noteStart = findFirstNoteSectionRowIndex(allRows, 0)
  if (noteStart >= 0) {
    tableEnd = noteStart
    note = collectNoteRows(allRows, noteStart)
  }

  return {
    name: ws.name,
    rows: copyObject(allRows.slice(0, tableEnd)),
    note,
  }
}
